import React, { useMemo } from "react";
import { Container, Header, Logo } from "./styles";
import logoUVerify from "assets/img/Uverify.png";
import { Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import TutorialContent from "components/UI/TutorialComponent";
import SidebarDocumentation, {
  formatTopicForDisplay,
} from "components/UI/SidebarPro/SidebarDocumentation";
import { MuiIconName } from "utils/renderIcons/MuiIconName";

const markdownContent = `
# **Liberação de Visualização de Atividades por Nível**

---

## 1. **Descrição Geral**
Este documento descreve as regras de liberação e visualização de atividades por níveis implementadas para a Acelen no sistema Uverify. As atividades são classificadas em níveis de 1 a 5, e sua apresentação aos usuários segue uma lógica progressiva baseada na conclusão das atividades de níveis anteriores. Além disso, cada nível possui uma nomenclatura específica, que reflete a terminologia adotada pela Acelen para fins de negócio.

---

## 2. **Diagrama**

![Liberação de atividades por nível.png](/assets/items.png)

---

## 3. **Regras de Liberação por Nível**

As atividades são apresentadas de acordo com a seguinte lógica:

### 3.1. **Nível 1**
- Atividades iniciais chamadas **Atividades de Mobilização**.
- **Regra de Liberação**: O usuário deve completar todas as atividades de nível 1 para que o nível 2 seja habilitado.

### 3.2. **Nível 2**
- Conhecidos como **Atividades de Documentação Solidária**.
- **Regra de Liberação**: Ao completar todas as atividades de nível 2, as atividades dos níveis 3 e 4 são habilitadas simultaneamente.

### 3.3. **Nível 3**
- Conhecidos como **Atividades de Documentação Solidária**.
- **Regra de Liberação**: Ao completar todas as atividades de nível 3, não tem nenhum efeito prático pois as atividades de nível 4 já estão liberadas.

### 3.4. **Nível 4**
- Também parte das **Atividades de Desmobilização**, geralmente contendo apenas a atividade **T_04_003**.
- **Regra de Liberação**: Após a conclusão de todas as atividades de nível 4, as atividades do nível 5 são habilitadas.

### 3.5. **Nível 5**
- Conhecidas como **Atividades de Desmobilização**, assim como as do nível 4.
- **Regra de Liberação**: Este é o nível final e só é habilitado após a conclusão de todas as atividades de nível 4.

---

## 4. **Exceções e Regras Adicionais**

- **Permanência de Visibilidade**:
  - Uma vez habilitado, um nível permanecerá visível para o usuário, mesmo que:
    - Novas atividades sejam adicionadas a níveis anteriores.
    - Atividades de níveis anteriores sejam reabertas.
  - Isso garante que a visibilidade de um nível seja definitiva após sua liberação inicial.

---

## 5. **Nomenclatura Específica para Níveis**

A terminologia usada para cada nível é adaptada às necessidades de negócio da Acelen, conforme descrito abaixo:

| **Nível** | **Nomenclatura Usada**                   |
|-----------|------------------------------------------|
| Nível 1   | **Atividades de Mobilização**            |
| Níveis 2 e 3 | **Atividades de Documentação Solidária** |
| Níveis 4 e 5 | **Atividades de Desmobilização**        |

---

## 6. **Fluxo de Liberação por Nível**

| **Etapa**                                  | **Condição**                                                                  | **Resultado**                               |
|--------------------------------------------|-------------------------------------------------------------------------------|---------------------------------------------|
| Conclusão de todas as atividades do Nível 1 | Nível 2 é habilitado.                                                         | Atividades do Nível 2 são visíveis.         |
| Conclusão de todas as atividades do Nível 2 | Níveis 3 e 4 são habilitados simultaneamente.                                 | Atividades dos Níveis 3 e 4 são visíveis.   |
| Conclusão de todas as atividades do Nível 4 | Atividades do Nível 5 são habilitadas.                                        | Atividades do Nível 5 são visíveis.         |
| Atividades de níveis anteriores reabertas   | Não afeta a visibilidade de níveis já habilitados.                            | Níveis habilitados continuam visíveis.      |

---

## 7. **Exemplos Práticos**

### 7.1. **Fluxo Padrão**
1. O usuário completa todas as atividades de mobilização (Nível 1).
   - Resultado: Atividades do Nível 2 são habilitadas.
2. O usuário completa todas as atividades de documentação solidária (Nível 2).
   - Resultado: Atividades dos Níveis 3 e 4 são habilitadas.
3. O usuário conclui a atividade **T_04_003** (única no Nível 4).
   - Resultado: Atividades de desmobilização do Nível 5 são habilitadas.

### 7.2. **Fluxo com Reabertura de Atividades**
- Após habilitar o Nível 3, uma atividade de Nível 1 é reaberta.
  - **Resultado**: As atividades do Nível 3 continuam visíveis, mesmo com a pendência reaberta.

---

## 8. **Erros e Tratamento**

| **Erro**                      | **Descrição**                                        | **Tratamento**                                |
|-------------------------------|----------------------------------------------------|----------------------------------------------|
| Atividades não visíveis       | O usuário não consegue visualizar o próximo nível. | Verificar se todas as atividades do nível atual foram concluídas. |
| Pendências reabertas          | Atividades de níveis anteriores ficam pendentes.  | Garantir que as atividades dos níveis já habilitados continuem visíveis. |

---

## 9. **Considerações Finais**

Este modelo de liberação por níveis permite um fluxo ordenado e progressivo, ao mesmo tempo em que garante flexibilidade para lidar com pendências de atividades reabertas ou recém-criadas. A nomenclatura específica adotada para os níveis reflete a realidade do negócio da Acelen e facilita a compreensão por parte dos usuários finais.
`;
const markdownContent2 = `
# **Configuração de atividades - Acelen**

---

## 1. **Descrição Geral**
Este fluxo descreve a configuração para cada documento incluindo qual o nível e qual o departamento responsável pela aprovação de cada uma das atividades.

---

## 2. **Configuração das atividades**

| Código       | Nome                                                                                                 | Entidade (Uverify) | Nível | [Roles](https://dev.azure.com/Stratesys/U-Verify%20Project/_wiki/wikis/uVerify-Project.wiki/424/Acelen?anchor=**acessos-u-verify-(dev)**)           |
| ------------ | ---------------------------------------------------------------------------------------------------- | ------------------ | ----- | --------------- |
| C_01_001     | Anexar Cadastro Nacional de Pessoa Jurídica – CNPJ (anual)                                           | sow                | 1     | gestaoTerceiros |
| C_01_002     | Anexar Certidão Negativa de Débitos Trabalhistas (anual)                                             | sow                | 1     | gestaoTerceiros |
| C_01_003     | Anexar Certidão Negativa do FGTS (anual)                                                             | sow                | 1     | gestaoTerceiros |
| C_01_004     | Anexar CCT vigente                                                                                   | sow                | 1     | gestaoTerceiros |
| C_01_005     | Anexar PCMSO                                                                                         | sow                | 1     | ssma            |
| C_01_006     | Anexar PGR                                                                                           | sow                | 1     | ssma            |
| C_01_007     | Anexar Certidão Negativa da Previdência Social (anual)                                               | sow                | 1     | gestaoTerceiros |
| C_01_008     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 01                                 | sow                | 1     | gestaoTerceiros |
| C_01_009     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 02                                 | sow                | 1     | gestaoTerceiros |
| C_01_010     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 03                                 | sow                | 1     | gestaoTerceiros |
| C_01_011     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 04                                 | sow                | 1     | gestaoTerceiros |
| C_01_012     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 05                                 | sow                | 1     | gestaoTerceiros |
| C_01_013     | Adicionar Cópia do Contrato vigente (em caso de subcontratação) - 06                                 | sow                | 1     | gestaoTerceiros |
| C_01_ESP_003 | Anexar PPR                                                                                           | sow                | 1     | ssma            |
| C_01_ESP_001 | Anexar PCA                                                                                           | sow                | 1     | ssma            |
| C_02_001     | Anexar Composição da Guia da Previdência Social (mensal)                                             | sow                | 2     | gestaoTerceiros |
| C_02_002     | Anexar Cópia da Guia da Previdência Social (mensal)                                                  | sow                | 2     | gestaoTerceiros |
| C_02_003     | Anexar Cópia da folha de pagamento do trabalhador                                                    | sow                | 2     | gestaoTerceiros |
| C_02_004     | Anexar Cópia da Guia do Recolhimento do FGTS                                                         | sow                | 2     | gestaoTerceiros |
| C_02_005     | Anexar NF de Serviços prestados à Acelen (mensal)                                                    | sow                | 2     | gestaoTerceiros |
| C_02_006     | Anexar Relação de empregados (RE) do FGTS (mensal)                                                   | sow                | 2     | gestaoTerceiros |
| C_02_ESP_001 | Anexar Nada Consta de Encerramento do Contrato                                                       | sow                | 2     | gestaoTerceiros |
| T_01_001     | Anexar Certidão Negativa de Justiça criminal estadual -  Documento legível em PDF.                   | worker             | 1     | credenciamento  |
| T_01_002     | Anexar Certidão Negativa de Justiça criminal federal -  Documento legível em PDF.                    | worker             | 1     | credenciamento  |
| T_01_003     | Anexar comprovante de residência legível em formato PDF.                                             | worker             | 1     | credenciamento  |
| T_01_004     | Anexar cópia do CPF, legível em formato PDF.                                                         | worker             | 1     | credenciamento  |
| T_01_005     | Anexar cópia do documento de identificação válido                                                    | worker             | 1     | credenciamento  |
| T_01_006     | Anexar foto padrão documento em formato de imagem                                                    | worker             | 1     | credenciamento  |
| T_01_007     | Anexar CNH válida em PDF                                                                             | worker             | 1     | credenciamento  |
| PTRAN        | Permissão de Trânsito                                                                                | worker             | 1     | N/A             |
| T_02_001     | Anexar ASO - Atestado de Saúde Ocupacional                                                           | worker             | 2     | ssma            |
| T_02_002     | Anexar cópia da CTPS - Documento padrão, legível e em formato PDF                                    | worker             | 2     | gestaoTerceiros |
| T_02_003     | Anexar Carteira do Convênio Médico                                                                   | worker             | 2     | ssma            |
| T_02_004     | Anexar cópia do contrato social - Documento padrão, legível e em formato PDF                         | worker             | 2     | gestaoTerceiros |
| T_02_005     | Anexar Certificado de NR-20                                                                          | worker             | 2     | ssma            |
| T_02_006     | Anexar cópia do contrato de trabalho - Documento padrão, legível e em formato PDF                    | worker             | 2     | gestaoTerceiros |
| T_02_007     | Anexar Certificado de função                                                                         | worker             | 2     | gestaoTerceiros |
| T_02_ESP_001 | Anexar NR 10 - Segurança em Instal. e Serviços em Eletricidade - Básico                              | worker             | 2     | ssma            |
| T_02_ESP_003 | Anexar NR 11 - Transporte, Movimentação, Armazenagem e Manuseio de Materiais                         | worker             | 2     | ssma            |
| T_02_ESP_004 | Anexar NR 12 - Segurança em Máquinas e Equipamentos                                                  | worker             | 2     | ssma            |
| T_02_ESP_005 | Anexar NR 33 - Supervisor de Espaço Confinado                                                        | worker             | 2     | ssma            |
| T_02_ESP_006 | Anexar NR 33 - Vigia e Entrante para Trabalhos em Espaço Confinado                                   | worker             | 2     | ssma            |
| T_02_ESP_007 | Anexar NR 35 - Trabalho em Altura                                                                    | worker             | 2     | ssma            |
| T_02_ESP_008 | Anexar NR 20 - 32h                                                                                   | worker             | 2     | ssma            |
| INTEGRA      | Integração                                                                                           | worker             | 2     | N/A             |
| T_03_001     | Anexar cópia do comprovante de disponibilização de benefícios fornecido ao trabalhador - Documento l | worker             | 3     | gestaoTerceiros |
| T_03_002     | Anexar cópia do comprovante de pagamento salarial - Documento legível em formato PDF                 | worker             | 3     | gestaoTerceiros |
| T_03_003     | Anexar cópia do registro de controle de jornada do trabalhador - Documento legível em formato PDF    | worker             | 3     | gestaoTerceiros |
| TRC          | Termo de Responsabilidade do Credenciado                                                             | worker             | 3     |                 |
| T_04_003     | Anexar Formulário de devolução da credencial                                                         | worker             | 4     | credenciamento  |
| T_04_001     | Anexar cópia do ASO demissional assinado pelo trabalhador                                            | worker             | 5     | gestaoTerceiros |
| T_04_002     | Anexar cópia da CTPS com informação da rescisão contratual - Documento em PDF                        | worker             | 5     | gestaoTerceiros |
| T_05_001     | Anexar comprovante de quitação das verbas rescisórias ao trabalhador                                 | worker             | 5     | gestaoTerceiros |
| T_05_002     | Anexar comprovante de pagamento do valor líquido rescisório                                          | worker             | 5     | gestaoTerceiros |
| T_05_003     | Anexar comprovante da Guia de recolhimento do FGTS rescisório e comprovação de depósito              | worker             | 5     | gestaoTerceiros |
| T_05_004     | Anexar Termo de Rescisão ou Carta de Pedido de Demissão escrita pelo trabalhador em próprio punho    | worker             | 5     | gestaoTerceiros |
| T_05_005     | Comprovante de transferência do local de prestação de serviços, assinado pelo empregado              | worker             | 5     | gestaoTerceiros |
| V_01_001     | Anexar formulário de Permissão de Transito                                                           | equipment          | 1     | credenciamento  |
| V_01_002     | Anexar Termo de Compromisso - Diretrizes para Condução Interna                                       | equipment          | 1     | credenciamento  |
| V_01_003     | Anexar Certificado curso Direção Defensiva                                                           | equipment          | 1     | credenciamento  |
| V_01_004     | Anexar cópia da CNH válida do condutor associado ao veículo                                          | equipment          | 1     | credenciamento  |
| V_01_006     | Anexar Formulário de Devolução da PTRAN                                                              | equipment          | 1     | credenciamento  |
| V_01_005     | Anexar Cópia do Documento do Veículo CRLV                                                            | equipment          | 2     | credenciamento  |

---


## 3. **Regras de Negócio**

*   As atividades são apresentadas de forma progressiva no Uverify de acordo com seu nível.
*   Quando todas atividades de um nível são completas (SAP Fieldglass) as atividades do próximo* nível são apresentadas.
*   Quando uma atividade é aprovada, uma nova verificação de liberação de nível é realizada.

---

## 4. **Possíveis Erros e Tratamento**

| **Erro** | **Descrição** | **Tratamento** |
| --- | --- | --- |
| Submissão de Documento Inválido | O fornecedor submete um documento com formato ou dados inválidos. | O departamento responsável deve rejeitar o documento. |
| Falha na Integração com SAP Fieldglass | Não foi possível completar a atividade no SAP Fieldglass. | Tentar novamente (máximo de 6 vezes) e registrar erro no log. |

---

## 5. **Referências e Links**
*   [Documentação da Fila de Retentativas](https://dev.azure.com/Stratesys/U-Verify%20Project/_wiki/wikis/uVerify-Project.wiki?wikiVersion=GBwikiMaster&pagePath=/Clients/Acelen/URL_Documenta%C3%A7%C3%A3o)
*   [Guia de Configuração de E-mails](https://dev.azure.com/Stratesys/U-Verify%20Project/_wiki/wikis/uVerify-Project.wiki?wikiVersion=GBwikiMaster&pagePath=/Clients/Acelen/URL_Guia)

`;

export interface IListSections {
  section: string;
  iconName?: MuiIconName;
  markdown: string;
  listTopics: string[];
}

// Função para extrair títulos de um conteúdo Markdown
const extractMarkdownTitles = (markdown: string): string[] => {
  const regex = /^(#+)\s*(.+)$/gm; // Regex para capturar cabeçalhos em Markdown
  const titles: string[] = [];
  let match;

  while ((match = regex.exec(markdown)) !== null) {
    // match[2] contém o título sem os # iniciais
    titles.push(match[2].trim());
  }

  return titles;
};

const listSections: IListSections[] = [
  {
    section: "",
    // iconName: "Settings",
    markdown: markdownContent,
    listTopics: [],
  },
  {
    section: "",
    markdown: markdownContent2,
    listTopics: [],
  },
];

const Documentation = () => {
  const navigate = useNavigate();

  const [logo] = React.useState(logoUVerify);
  const [openMarkDown, setOpenMarkDown] = React.useState("");

  const listSectionsFormatted = useMemo(() => {
    const newList: IListSections[] = listSections.map((section) => {
      const list = extractMarkdownTitles(section.markdown);
      const sectionSidebar = list[0];
      return {
        ...section,
        section: sectionSidebar,
        listTopics: list,
      };
    });
    return newList;
  }, [listSections]);

  return (
    <Container>
      <Header>
        <Button
          variant="text"
          color="inherit"
          sx={{ color: "#ff6700" }}
          size="medium"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/")}
        >
          Voltar para o Uverify
        </Button>
        <Logo src={logo} alt="Logo U-Verify" />
      </Header>
      <SidebarDocumentation
        listSections={listSectionsFormatted}
        setMarkDown={setOpenMarkDown}
      >
        <TutorialContent markdown={openMarkDown} />
      </SidebarDocumentation>
    </Container>
  );
};

export default Documentation;
